import { post } from 'app/shared/utils/fetchr';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { GetTransactionsPayload } from 'app/modules/transactions/requests';
import { ALERT_QUERY_KEYS } from 'app/modules/alerts/queries/keys';
import { ShortTxnResponse } from 'app/modules/transactions/types';
import { useSelector } from 'react-redux';
import { selectDLEnabledForAlertTxnList } from 'app/shared/featureFlags/selectors';
import { useEffect, useState } from 'react';
import { ColdStorageLoadingStatus } from 'app/shared/utils/fastArchHelpers';
import { selectHasReadAlertsPermission } from 'app/modules/session/selectors';
import { useGetAlert } from 'app/modules/alerts/queries/useGetAlert';

const fetchTransactionsByAlert = (
  id: number,
  body: GetTransactionsPayload,
): Promise<ShortTxnResponse[]> => post(`/v1/alerts/${id}/transactions`, body);

export const fetchTransactionsByAlertFromCache = (
  id: number,
  body: GetTransactionsPayload,
): Promise<ShortTxnResponse[]> => {
  return post(`/v1/alerts/cached/${id}/transactions`, body);
};

export const useGetAlertTransactions = (
  id: number,
  body: GetTransactionsPayload,
  options: Pick<UseQueryOptions<ShortTxnResponse[]>, 'enabled'> = {
    enabled: true,
  },
) => {
  const [prevColdStorageLoadingStatus, setPrevColdStorageLoadingStatus] =
    useState<ColdStorageLoadingStatus | null>(null);
  const useCacheEndpoint = useSelector(selectDLEnabledForAlertTxnList);
  const { coldStorageLoading } = useGetAlert(id, {
    enabled: useCacheEndpoint,
  });
  const hasReadAlertsPermission = useSelector(selectHasReadAlertsPermission);
  const queryEnabled = hasReadAlertsPermission && body.limit !== 0 && id !== -1;

  const query = useQuery({
    queryKey: ALERT_QUERY_KEYS.getAlertTransactions(id, body, useCacheEndpoint),
    queryFn: () =>
      useCacheEndpoint
        ? fetchTransactionsByAlertFromCache(id, body)
        : fetchTransactionsByAlert(id, body),
    meta: { errorMessage: 'Failed to fetch transactions for alert.' },
    enabled: queryEnabled && options.enabled,
    staleTime: 1000,
  });

  const { refetch } = query;
  useEffect(() => {
    // Re fetch transactions if we were waiting for the transactions to load from cold storage
    const wasLoading =
      prevColdStorageLoadingStatus !== null &&
      prevColdStorageLoadingStatus !== ColdStorageLoadingStatus.LOADED;
    if (
      queryEnabled &&
      useCacheEndpoint &&
      wasLoading &&
      coldStorageLoading.txn_event === ColdStorageLoadingStatus.LOADED
    ) {
      refetch();
    }
    setPrevColdStorageLoadingStatus(coldStorageLoading.txn_event);
  }, [
    coldStorageLoading.txn_event,
    prevColdStorageLoadingStatus,
    useCacheEndpoint,
    queryEnabled,
    refetch,
  ]);

  return query;
};
